import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import { numberID, rupiah, formatDate, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Boxes, Layers, ShieldCheck, ShieldAlert, Search, X, Coins, History } from "lucide-react";

// ---- shared helpers ----
export const fmtKg = (kg) => Number(kg || 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rollKg = (r) => (r.remaining_weight_g != null ? r.remaining_weight_g / 1000 : (r.remaining_weight_kg || 0));
const rollOrigKg = (r) => (r.original_weight_g != null ? r.original_weight_g / 1000 : (r.original_weight_kg || 0));

const ROLL_STATUS = {
  AVAILABLE: { label: "Tersedia", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  PARTIAL: { label: "Sisa Roll", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  QUARANTINE: { label: "Ditahan", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  ISSUED_TO_CUTTING: { label: "Di Cutting", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  CONSUMED: { label: "Habis", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  RETURNED_SUPPLIER: { label: "Retur Supplier", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  INACTIVE: { label: "Nonaktif", cls: "bg-slate-100 text-slate-400 border-slate-200" },
};

export function RollStatusBadge({ status }) {
  const m = ROLL_STATUS[status] || { label: status || "-", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  return <Badge variant="outline" className={m.cls} data-testid={`roll-status-${status}`}>{m.label}</Badge>;
}

// ---- compact cell for the Inventory Konveksi list ----
export function CwStockCell({ summary, rollQty }) {
  return (
    <div className="text-right" data-testid="cw-stock-cell">
      <p className="font-bold text-slate-900">{numberID(rollQty || 0)} <span className="text-xs font-medium text-slate-500">Roll</span></p>
      <p className="text-xs font-semibold text-indigo-600">{summary ? fmtKg(summary.usable_weight_kg) : "0,00"} KG usable</p>
      {summary && summary.quarantine_weight_g > 0 && (
        <p className="text-[11px] text-orange-600" data-testid="cw-stock-quarantine">{fmtKg(summary.quarantine_weight_kg)} KG ditahan</p>
      )}
    </div>
  );
}

export function CwMinimumCell({ item }) {
  const minRoll = item.minimum_stock || 0;
  const minKg = item.min_weight_kg;
  return (
    <div className="text-right text-slate-400" data-testid="cw-minimum-cell">
      <p>{numberID(minRoll)} <span className="text-[11px]">Roll</span></p>
      {minKg != null && minKg !== "" && <p className="text-[11px]">{fmtKg(minKg)} KG</p>}
    </div>
  );
}

// ---- Ringkasan tab ----
function Ringkasan({ item, summary, consistency }) {
  if (!summary) return <Loading />;
  const Row = ({ label, value, hint }) => (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5">
      <div><p className="text-xs text-slate-500">{label}</p>{hint && <p className="text-[11px] text-slate-400">{hint}</p>}</div>
      <p className="text-right text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="cw-tab-ringkasan">
      <ConsistencyIndicator consistency={consistency} />
      <div className="grid grid-cols-3 gap-3">
        <Card label="Roll Tersedia Saat Ini" value={numberID(summary.usable_roll_count ?? ((summary.available_roll_count || 0) + (summary.partial_roll_count || 0)))} suffix="Roll" accent="indigo" testId="cw-ring-usable-roll" />
        <Card label="Usable KG Saat Ini" value={fmtKg(summary.usable_weight_kg)} suffix="KG" accent="indigo" testId="cw-ring-usable" />
        <Card label="Quarantine KG" value={fmtKg(summary.quarantine_weight_kg)} suffix="KG" accent="orange" testId="cw-ring-quarantine" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <Row label="Item" value={item.name} />
        <Row label="Roll Tercatat (Semua Status)" value={numberID(summary.total_roll_count)} hint="Registri/historis — bukan stok tersedia" />
        <Row label="Physical KG" value={`${fmtKg(summary.physical_weight_kg)} KG`} hint="Usable + Quarantine" />
        <Row label="Roll Tersedia (AVAILABLE)" value={numberID(summary.available_roll_count)} />
        <Row label="Roll Sisa (PARTIAL)" value={numberID(summary.partial_roll_count)} />
        <Row label="Roll Ditahan (QUARANTINE)" value={numberID(summary.quarantine_roll_count)} />
        <Row label="Rata-rata Usable / Roll" value={`${fmtKg(summary.avg_usable_weight_kg_per_roll)} KG`} hint="Dari roll usable (AVAILABLE+PARTIAL)" />
        <Row label="Minimum Roll" value={item.minimum_stock ? `${numberID(item.minimum_stock)} Roll` : "belum diatur"} />
        <Row label="Minimum KG" value={item.min_weight_kg != null && item.min_weight_kg !== "" ? `${fmtKg(item.min_weight_kg)} KG` : "belum diatur"} />
      </div>
    </div>
  );
}

const ACCENT = { slate: "text-slate-900", indigo: "text-indigo-600", orange: "text-orange-600", emerald: "text-emerald-600" };
function Card({ label, value, suffix, accent = "slate", testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`mt-1 font-display text-xl font-bold ${ACCENT[accent]}`}>{value} <span className="text-xs font-medium text-slate-400">{suffix}</span></p>
    </div>
  );
}

function ConsistencyIndicator({ consistency }) {
  if (!consistency) return null;
  const variance = consistency.cache_variance_kg;
  const ok = variance == null || Math.abs(variance) < 0.001;
  return (
    <div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${ok ? "border-emerald-200 bg-emerald-50/60 text-emerald-800" : "border-amber-200 bg-amber-50/60 text-amber-800"}`} data-testid="cw-consistency">
      {ok ? <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> : <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />}
      <div>
        <p className="font-semibold">Integritas Catch Weight: {ok ? "OK" : "Variance Terdeteksi"}</p>
        <p className="text-xs">
          Otoritatif (fabric_rolls): <b>{fmtKg(consistency.physical_weight_kg)} KG</b> fisik
          {!ok && <> · cache lama (deprecated): {consistency.deprecated_cache_weight_on_hand_kg == null ? "—" : `${fmtKg(consistency.deprecated_cache_weight_on_hand_kg)} KG`} · selisih {fmtKg(variance)} KG (hanya laporan, tidak dikoreksi otomatis)</>}
        </p>
      </div>
    </div>
  );
}

// ---- Daftar Roll tab ----
function DaftarRoll({ rolls, onPick }) {
  const [f, setF] = useState({ status: "all", supplier: "all", date: "", q: "" });
  const suppliers = useMemo(() => Array.from(new Set((rolls || []).map((r) => r.supplier_name).filter(Boolean))), [rolls]);
  const filtered = useMemo(() => (rolls || []).filter((r) => {
    if (f.status !== "all" && r.status !== f.status) return false;
    if (f.supplier !== "all" && r.supplier_name !== f.supplier) return false;
    if (f.date && (r.received_at || "").slice(0, 10) !== f.date) return false;
    if (f.q.trim()) {
      const q = f.q.trim().toLowerCase();
      const hay = [r.roll_no, r.supplier_lot, r.po_no, r.gr_no].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [rolls, f]);
  if (!rolls) return <Loading />;
  return (
    <div className="space-y-3" data-testid="cw-tab-rolls">
      <div className="flex flex-wrap items-end gap-2">
        <div><Label className="text-xs text-slate-500">Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="mt-1 h-9 w-[150px]" data-testid="roll-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Status</SelectItem>{Object.keys(ROLL_STATUS).map((s) => <SelectItem key={s} value={s}>{ROLL_STATUS[s].label}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs text-slate-500">Supplier</Label>
          <Select value={f.supplier} onValueChange={(v) => setF({ ...f, supplier: v })}><SelectTrigger className="mt-1 h-9 w-[150px]" data-testid="roll-filter-supplier"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Supplier</SelectItem>{suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs text-slate-500">Tanggal Terima</Label><Input type="date" className="mt-1 h-9 w-[150px]" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="roll-filter-date" /></div>
        <div className="flex-1 min-w-[180px]"><Label className="text-xs text-slate-500">Cari (Roll No / Lot / PO / GR)</Label>
          <Input className="mt-1 h-9" placeholder="cari..." value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="roll-search" /></div>
        <Button variant="ghost" className="h-9" onClick={() => setF({ status: "all", supplier: "all", date: "", q: "" })} data-testid="roll-reset"><X className="mr-1 h-4 w-4" /> Reset</Button>
      </div>
      <p className="text-xs text-slate-500" data-testid="roll-count">{filtered.length} roll</p>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {filtered.length === 0 ? <EmptyState title="Tidak ada roll" subtitle="Belum ada roll sesuai filter." icon={Layers} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Roll No</TableHead><TableHead>Supplier</TableHead><TableHead>Lot</TableHead><TableHead>Tgl Terima</TableHead>
              <TableHead>PO</TableHead><TableHead>GR</TableHead>
              <TableHead className="text-right">Berat Awal</TableHead><TableHead className="text-right">Sisa</TableHead>
              <TableHead className="text-right">Cost/KG</TableHead><TableHead className="text-right">Nilai Sisa</TableHead>
              <TableHead>Status</TableHead><TableHead>Lokasi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onPick(r)} data-testid={`roll-row-${r.roll_no}`}>
                  <TableCell className="font-mono text-xs font-semibold text-indigo-700">{r.roll_no}</TableCell>
                  <TableCell className="text-slate-600">{r.supplier_name || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.supplier_lot || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(r.received_at)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.po_no || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.gr_no || "-"}</TableCell>
                  <TableCell className="text-right">{fmtKg(rollOrigKg(r))} KG</TableCell>
                  <TableCell className="text-right font-semibold">{fmtKg(rollKg(r))} KG</TableCell>
                  <TableCell className="text-right text-xs">{rupiah(r.unit_cost_per_kg)}</TableCell>
                  <TableCell className="text-right">{rupiah(r.remaining_value)}</TableCell>
                  <TableCell><RollStatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-xs text-slate-400">{r.location || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ---- Riwayat tab (read-only, reuses inventory transactions) ----
const TXN_LABEL = { PURCHASE_RECEIPT: "Penerimaan Pembelian", IN: "Barang Masuk", OUT: "Barang Keluar",
  ADJUSTMENT: "Penyesuaian", STOCK_OPNAME: "Stock Opname", MATERIAL_USAGE: "Pemakaian Bahan", PRODUCTION_USAGE: "Pemakaian Produksi" };
function Riwayat({ warehouseId, itemId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/inventory/transactions", { params: { warehouse_id: warehouseId, item_id: itemId, limit: 200 } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [warehouseId, itemId]);
  if (!rows) return <Loading />;
  if (rows.length === 0) return <EmptyState title="Belum ada riwayat" subtitle="Belum ada pergerakan stok untuk item ini." icon={History} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" data-testid="cw-tab-riwayat">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead className="text-right">Masuk</TableHead>
          <TableHead className="text-right">Keluar</TableHead><TableHead className="text-right">Sesudah</TableHead>
          <TableHead>Referensi</TableHead><TableHead>Catatan</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.id} data-testid={`cw-txn-${t.id}`}>
              <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(t.created_at)}</TableCell>
              <TableCell className="text-xs">{TXN_LABEL[t.type] || t.type}</TableCell>
              <TableCell className="text-right font-semibold text-emerald-600">{t.qty > 0 ? numberID(t.qty) : ""}</TableCell>
              <TableCell className="text-right font-semibold text-rose-600">{t.qty < 0 ? numberID(Math.abs(t.qty)) : ""}</TableCell>
              <TableCell className="text-right">{numberID(t.qty_after)}</TableCell>
              <TableCell className="text-xs text-slate-500">{typeof t.reference === "string" ? t.reference : "-"}</TableCell>
              <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{t.notes || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---- Cost tab (derived from fabric_rolls, authoritative) ----
function CostTab({ rolls, summary }) {
  if (!rolls || !summary) return <Loading />;
  const usable = rolls.filter((r) => ["AVAILABLE", "PARTIAL"].includes(r.status));
  const costs = Array.from(new Set(rolls.map((r) => r.unit_cost_per_kg).filter((x) => x != null)));
  const lastRoll = [...rolls].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
  const avgCost = summary.usable_weight_kg > 0 ? summary.usable_remaining_value / summary.usable_weight_kg : null;
  const Row = ({ label, value, hint }) => (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5">
      <div><p className="text-xs text-slate-500">{label}</p>{hint && <p className="text-[11px] text-slate-400">{hint}</p>}</div>
      <p className="text-right text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="cw-tab-cost">
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-sm">
        <Coins className="h-4 w-4 text-emerald-600" />
        <span className="text-slate-600">Nilai dihitung dari <b>fabric_rolls</b> (otoritatif) — bukan cache lama.</span>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <Row label="Harga Beli Terakhir / KG" value={lastRoll ? rupiah(lastRoll.unit_cost_per_kg) : "Belum tersedia"} hint="Dari roll terbaru (GR)" />
        <Row label="Average Cost / KG (usable)" value={avgCost != null ? rupiah(avgCost) : "Belum tersedia"} hint="Nilai usable ÷ KG usable" />
        <Row label="Cost / KG per Roll" value={costs.length ? costs.map((c) => rupiah(c)).join(", ") : "Belum tersedia"} />
        <Row label="Nilai Persediaan Usable" value={rupiah(summary.usable_remaining_value)} hint="Σ(sisa KG × cost/KG) status AVAILABLE+PARTIAL" />
        <Row label="Nilai Quarantine (terpisah)" value={rupiah(summary.quarantine_remaining_value)} hint="Ditahan — tidak dihitung sebagai usable" />
      </div>
      <p className="text-[11px] text-slate-500">{usable.length} roll usable dari {rolls.length} roll total.</p>
    </div>
  );
}

// ---- Roll detail drawer (read-only) ----
function RollDrawer({ roll, itemName, onClose }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get(`/production/catch-weight/rolls/${roll.id}/events`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [roll.id]);
  const EV_LABEL = { ISSUED_TO_CUTTING: "Di-issue ke Cutting", RECONCILE_PARTIAL: "Rekonsiliasi — Sisa Roll",
    RECONCILE_CONSUMED: "Rekonsiliasi — Habis", RECONCILE_AVAILABLE: "Rekonsiliasi — Kembali Utuh" };
  const Row = ({ label, value }) => (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-right text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="roll-drawer">
        <SheetHeader><SheetTitle className="font-display flex items-center gap-2"><Layers className="h-5 w-5 text-indigo-600" /> {roll.roll_no}</SheetTitle></SheetHeader>
        <div className="mt-3"><RollStatusBadge status={roll.status} /></div>
        <div className="mt-4">
          <Row label="Item" value={itemName} />
          <Row label="Barcode" value={roll.barcode || "-"} />
          <Row label="Supplier" value={roll.supplier_name || "-"} />
          <Row label="Supplier Lot" value={roll.supplier_lot || "-"} />
          <Row label="PO" value={roll.po_no || "-"} />
          <Row label="GR" value={roll.gr_no || "-"} />
          <Row label="Tanggal Terima" value={formatDate(roll.received_at)} />
          <Row label="Berat Awal" value={`${fmtKg(rollOrigKg(roll))} KG`} />
          <Row label="Sisa Berat" value={`${fmtKg(rollKg(roll))} KG`} />
          <Row label="Cost / KG" value={rupiah(roll.unit_cost_per_kg)} />
          <Row label="Nilai Awal" value={rupiah(roll.original_value)} />
          <Row label="Nilai Sisa" value={rupiah(roll.remaining_value)} />
          <Row label="Lokasi" value={roll.location || "-"} />
        </div>
        <div className="mt-5" data-testid="roll-lifecycle">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Riwayat Lifecycle Roll</p>
          {!events ? <p className="text-xs text-slate-400">Memuat…</p> : (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-xs"><span className="mt-0.5 h-2 w-2 rounded-full bg-emerald-500" /><div><p className="font-semibold text-slate-700">Diterima (RECEIVED)</p><p className="text-slate-400">{formatDate(roll.received_at)} · Berat awal {fmtKg(rollOrigKg(roll))} KG</p></div></div>
              {events.map((e) => (
                <div key={e.id} className="flex items-start gap-2 text-xs"><span className="mt-0.5 h-2 w-2 rounded-full bg-indigo-500" /><div><p className="font-semibold text-slate-700">{EV_LABEL[e.event] || e.event}</p><p className="text-slate-400">{formatDate(e.at)} · {fmtKg((e.weight_g || 0) / 1000)} KG{e.fri_no ? ` · ${e.fri_no}` : ""}{e.by ? ` · ${e.by}` : ""}</p></div></div>
              ))}
              {events.length === 0 && <p className="text-xs text-slate-400">Belum ada pergerakan lifecycle (roll masih di gudang).</p>}
            </div>
          )}
        </div>
        <p className="mt-4 text-[11px] text-slate-400">Tampilan read-only. Perubahan stok roll dilakukan melalui alur operasional (Cutting/Return) yang akan datang.</p>
      </SheetContent>
    </Sheet>
  );
}

// ---- main detail dialog ----
export function CwItemDetailDialog({ item, warehouseId, onClose }) {
  const [rolls, setRolls] = useState(null);
  const [summary, setSummary] = useState(null);
  const [consistency, setConsistency] = useState(null);
  const [pickRoll, setPickRoll] = useState(null);
  useEffect(() => {
    api.get("/purchasing/fabric-rolls", { params: { inventory_item_id: item.id, warehouse_id: warehouseId } }).then((r) => setRolls(r.data)).catch((e) => { toast.error(apiError(e)); setRolls([]); });
    api.get("/purchasing/fabric-rolls/summary", { params: { inventory_item_id: item.id, warehouse_id: warehouseId } }).then((r) => setSummary(r.data)).catch(() => setSummary(null));
    api.get("/purchasing/fabric-rolls/consistency", { params: { inventory_item_id: item.id } }).then((r) => setConsistency((r.data.items || [])[0] || null)).catch(() => setConsistency(null));
  }, [item.id, warehouseId]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="cw-detail-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Boxes className="h-5 w-5 text-indigo-600" /> {item.name} <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600">Catch Weight</span></DialogTitle></DialogHeader>
        <Tabs defaultValue="ringkasan">
          <TabsList>
            <TabsTrigger value="ringkasan" data-testid="cw-tab-ringkasan-trigger">Ringkasan</TabsTrigger>
            <TabsTrigger value="rolls" data-testid="cw-tab-rolls-trigger">Daftar Roll</TabsTrigger>
            <TabsTrigger value="riwayat" data-testid="cw-tab-riwayat-trigger">Riwayat</TabsTrigger>
            <TabsTrigger value="cost" data-testid="cw-tab-cost-trigger">Cost</TabsTrigger>
          </TabsList>
          <TabsContent value="ringkasan" className="mt-4"><Ringkasan item={item} summary={summary} consistency={consistency} /></TabsContent>
          <TabsContent value="rolls" className="mt-4"><DaftarRoll rolls={rolls} onPick={setPickRoll} /></TabsContent>
          <TabsContent value="riwayat" className="mt-4"><Riwayat warehouseId={warehouseId} itemId={item.id} /></TabsContent>
          <TabsContent value="cost" className="mt-4"><CostTab rolls={rolls} summary={summary} /></TabsContent>
        </Tabs>
        {pickRoll && <RollDrawer roll={pickRoll} itemName={item.name} onClose={() => setPickRoll(null)} />}
      </DialogContent>
    </Dialog>
  );
}
